/**
 * How the catalog is laid out: how many packages a page holds and whether they are tiles or rows.
 *
 * Both are display preferences of the browser in use, not facts about a tenant, so unlike the
 * remembered tab (see catalogTab.ts) one value serves every user and tenant on this machine.
 */

export const PAGE_SIZES = [12, 24, 48, 96] as const
export type PageSize = (typeof PAGE_SIZES)[number]
export const DEFAULT_PAGE_SIZE: PageSize = 24

export type CatalogView = 'tile' | 'list'
export const DEFAULT_CATALOG_VIEW: CatalogView = 'tile'

const PAGE_SIZE_KEY = 'aeam.catalog.pageSize'
const VIEW_KEY = 'aeam.catalog.view'

function readStored(key: string): string | null {
  try {
    return window.localStorage.getItem(key)
  } catch {
    // Private-browsing and storage-disabled profiles throw on read.
    return null
  }
}

function writeStored(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value)
  } catch {
    /* remembered for this page view only */
  }
}

/** The remembered page size, or the default when nothing valid is stored. */
export function readPageSize(): PageSize {
  const stored = Number(readStored(PAGE_SIZE_KEY))
  return (PAGE_SIZES as readonly number[]).includes(stored) ? (stored as PageSize) : DEFAULT_PAGE_SIZE
}

export function writePageSize(size: PageSize): void {
  writeStored(PAGE_SIZE_KEY, String(size))
}

/** The remembered layout, or tiles when nothing valid is stored. */
export function readCatalogView(): CatalogView {
  return readStored(VIEW_KEY) === 'list' ? 'list' : DEFAULT_CATALOG_VIEW
}

export function writeCatalogView(view: CatalogView): void {
  writeStored(VIEW_KEY, view)
}
